import { RiDownloadLine, RiLoader4Line, RiRefreshLine, RiUploadCloud2Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { $api } from "@/api/query";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { fetchCurrentUser } from "@/lib/auth";
import { isOrgAdmin } from "@/lib/role";

export const Route = createFileRoute("/_app/catalogue/stock")({
	// Admin-only: generating or uploading the feed is an org-wide action that
	// reaches Farfetch. The backend enforces OrgAdmin on the export endpoints;
	// this bounce keeps a member off a page they cannot use.
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!isOrgAdmin(user)) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: FarfetchStockPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExportLogEntry {
	id: string;
	target: string;
	filename: string;
	rowCount: number;
	skippedCount: number;
	exportedAt: string;
	triggeredBy: string;
	uploadPath?: string | null;
	uploadError?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(iso: string): string {
	const d = new Date(iso);
	return d.toLocaleString(undefined, {
		year: "numeric",
		month: "short",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function formatRelative(iso: string): string {
	const d = new Date(iso);
	const diffMs = Date.now() - d.getTime();
	const sec = Math.round(diffMs / 1000);
	if (sec < 60) return "just now";
	const min = Math.round(sec / 60);
	if (min < 60) return `${min} min ago`;
	const hr = Math.round(min / 60);
	if (hr < 24) return `${hr} h ago`;
	const days = Math.round(hr / 24);
	if (days < 7) return `${days} d ago`;
	return formatDateTime(iso);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FarfetchStockPage() {
	const queryClient = useQueryClient();
	const [exporting, setExporting] = useState(false);
	const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);

	// `isError`, not `error`: the OpenAPI schema declares the 403 with no body,
	// so the typed `error` collapses to `never` and TypeScript would treat the
	// error card as unreachable (typing `refetch` inside it as `never`).
	const { data, isLoading, isError, refetch } = $api.useQuery(
		"get",
		"/api/v1/catalogue/export-log",
		{ params: { query: { target: "farfetch_stock" } } },
	);

	const entries = useMemo(() => {
		return (data as ExportLogEntry[] | undefined) ?? [];
	}, [data]);

	const lastEntry = entries[0];

	function invalidateLog() {
		queryClient.invalidateQueries({
			queryKey: ["get", "/api/v1/catalogue/export-log"],
		});
	}

	/// Generate the stock CSV WITHOUT touching Farfetch's SFTP and hand it to
	/// the browser as a download. The export is still logged (Upload column
	/// shows "—").
	async function handleDownload() {
		setExporting(true);
		try {
			const response = await fetch("/api/v1/farfetch/stock-export?upload=false", {
				method: "POST",
				credentials: "include",
			});
			if (!response.ok) {
				toast.error("Export failed");
				return;
			}
			const blob = await response.blob();
			const filename =
				response.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ||
				"farfetch-stock.csv";
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			a.click();
			URL.revokeObjectURL(url);
			toast.success("CSV downloaded (nothing was uploaded to Farfetch)");
			invalidateLog();
		} catch {
			toast.error("Export failed");
		} finally {
			setExporting(false);
		}
	}

	/// Generate the stock CSV and push it to Farfetch's SFTP (the configured
	/// stock export directory). The response body is the CSV itself, so the
	/// upload outcome is read from the freshly refetched export log.
	async function handleUpload() {
		setExporting(true);
		try {
			const response = await fetch("/api/v1/farfetch/stock-export?upload=true", {
				method: "POST",
				credentials: "include",
			});
			if (!response.ok) {
				toast.error("Export failed");
				return;
			}
			const fresh = await refetch();
			const newest = ((fresh.data as ExportLogEntry[] | undefined) ?? [])[0];
			if (newest?.uploadError) {
				toast.error(`Upload failed: ${newest.uploadError}`);
			} else if (newest?.uploadPath) {
				toast.success(`Uploaded to Farfetch: ${newest.uploadPath}`);
			} else {
				toast.success("Export finished - check the log for the upload result");
			}
			invalidateLog();
		} catch {
			toast.error("Export failed");
		} finally {
			setExporting(false);
		}
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-start justify-between gap-4">
				<div>
					<h1 className="text-2xl font-semibold tracking-tight">Farfetch Stock</h1>
					<p className="text-sm text-muted-foreground">
						Generate the Farfetch stock/inventory CSV from in-stock rows. Review every export in the
						log below.
					</p>
					{lastEntry ? (
						<p className="text-xs text-muted-foreground mt-1">
							Last export: {lastEntry.rowCount} rows, {lastEntry.skippedCount} skipped —{" "}
							{formatRelative(lastEntry.exportedAt)}
						</p>
					) : (
						<p className="text-xs text-muted-foreground mt-1">No exports yet.</p>
					)}
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
						<RiRefreshLine className="size-4 mr-1.5" />
						Refresh
					</Button>
					<Button variant="outline" onClick={handleDownload} disabled={exporting}>
						{exporting ? (
							<RiLoader4Line className="mr-2 size-4 animate-spin" />
						) : (
							<RiDownloadLine className="mr-2 size-4" />
						)}
						Download CSV
					</Button>
					<Button onClick={() => setUploadConfirmOpen(true)} disabled={exporting}>
						{exporting ? (
							<RiLoader4Line className="mr-2 size-4 animate-spin" />
						) : (
							<RiUploadCloud2Line className="mr-2 size-4" />
						)}
						Upload to Farfetch
					</Button>
				</div>
			</div>

			<AlertDialog open={uploadConfirmOpen} onOpenChange={setUploadConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Upload stock to Farfetch?</AlertDialogTitle>
						<AlertDialogDescription>
							This generates the stock/inventory CSV from in-stock rows and uploads it to Farfetch's
							SFTP (the configured stock export directory). Farfetch will process the file - make
							sure the stock levels are final.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setUploadConfirmOpen(false);
								void handleUpload();
							}}
						>
							Upload
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Empty state CTA */}
			{!isLoading && entries.length === 0 && !isError && (
				<Card>
					<CardContent className="py-12 flex flex-col items-center gap-3 text-center">
						<RiUploadCloud2Line className="size-10 text-muted-foreground" />
						<div>
							<p className="font-medium">No exports yet</p>
							<p className="text-sm text-muted-foreground mt-1">
								Head to the{" "}
								<Link to="/catalogue/editor" className="underline underline-offset-2">
									Editor
								</Link>{" "}
								to review stock, then generate your first CSV.
							</p>
						</div>
						<Button onClick={handleDownload} disabled={exporting}>
							{exporting ? (
								<RiLoader4Line className="mr-2 size-4 animate-spin" />
							) : (
								<RiDownloadLine className="mr-2 size-4" />
							)}
							Download first CSV
						</Button>
					</CardContent>
				</Card>
			)}

			{/* Error state */}
			{isError && (
				<Card className="border-destructive/40 bg-destructive/5">
					<CardContent className="py-6 flex items-center justify-between">
						<div>
							<p className="text-sm font-medium">Failed to load export history</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								The server returned an error. Try refreshing.
							</p>
						</div>
						<Button variant="outline" size="sm" onClick={() => refetch()}>
							<RiRefreshLine className="size-4 mr-1.5" />
							Retry
						</Button>
					</CardContent>
				</Card>
			)}

			{/* Log table */}
			{(isLoading || entries.length > 0) && (
				<Card>
					<CardContent className="p-0">
						{isLoading ? (
							<div className="p-6 space-y-3">
								{Array.from({ length: 5 }).map((_, i) => (
									<Skeleton key={i} className="h-12 w-full" />
								))}
							</div>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Exported</TableHead>
										<TableHead>Trigger</TableHead>
										<TableHead>Filename</TableHead>
										<TableHead className="text-right">Rows</TableHead>
										<TableHead className="text-right">Skipped</TableHead>
										<TableHead>Upload</TableHead>
										<TableHead className="text-right">Status</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{entries.map((entry) => {
										const skipRatio =
											entry.rowCount + entry.skippedCount === 0
												? 0
												: entry.skippedCount / (entry.rowCount + entry.skippedCount);
										const status =
											skipRatio === 0
												? { label: "Clean", variant: "default" as const }
												: skipRatio < 0.1
													? { label: "OK", variant: "secondary" as const }
													: { label: "Many skips", variant: "destructive" as const };
										return (
											<TableRow key={entry.id}>
												<TableCell className="whitespace-nowrap">
													<div className="flex flex-col gap-0.5">
														<span className="text-sm font-medium">
															{formatRelative(entry.exportedAt)}
														</span>
														<span className="text-xs text-muted-foreground">
															{formatDateTime(entry.exportedAt)}
														</span>
													</div>
												</TableCell>
												<TableCell>
													<Badge
														variant={entry.triggeredBy === "scheduler" ? "secondary" : "outline"}
													>
														{entry.triggeredBy === "scheduler" ? "Auto" : "Manual"}
													</Badge>
												</TableCell>
												<TableCell className="font-mono text-xs break-all">
													{entry.filename}
												</TableCell>
												<TableCell className="text-right font-mono tabular-nums">
													{entry.rowCount}
												</TableCell>
												<TableCell className="text-right font-mono tabular-nums">
													{entry.skippedCount > 0 ? (
														<span className="text-yellow-600">{entry.skippedCount}</span>
													) : (
														<span className="text-muted-foreground">0</span>
													)}
												</TableCell>
												<TableCell className="max-w-[280px]">
													{entry.uploadError ? (
														<span
															className="text-xs text-destructive truncate block"
															title={entry.uploadError}
														>
															Failed: {entry.uploadError}
														</span>
													) : entry.uploadPath ? (
														<span
															className="text-xs font-mono text-muted-foreground truncate block"
															title={entry.uploadPath}
														>
															{entry.uploadPath}
														</span>
													) : (
														<span className="text-xs text-muted-foreground">—</span>
													)}
												</TableCell>
												<TableCell className="text-right">
													<Badge variant={status.variant}>{status.label}</Badge>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						)}
					</CardContent>
				</Card>
			)}
		</div>
	);
}
